import React from "react";
import { colorFor, hrefFor, labelFor } from "./palette";

const SACRED_CELL = {
  attested: "Ancient source",
  none_located: "None found",
  not_investigated: "Not checked"
};

// ONE THING'S correspondences as a table.
//
// The map is for wandering and the list is for reading; this is for comparing,
// which is the third thing people do with a table of correspondences and the
// one neither of the others is any good at. Same rows, same provenance, sorted
// by the same Prominence the map draws with.
export default function NodeTable({ atlas, node, onSelectEdge, schools }) {
  const rows = (atlas.edgesFor ? atlas.edgesFor(node.key) : [])
    .filter(Boolean)
    .sort((a, b) => (b.weight || 0) - (a.weight || 0));

  if (rows.length === 0) return null;

  return (
    <div className="correspondence-table__scroll">
      <table className="correspondence-table" data-testid="node-table">
        <caption className="visually-hidden">
          {`Everything ${node.name} is said to correspond to`}
        </caption>
        <thead>
          <tr>
            <th scope="col">Corresponds as</th>
            <th scope="col">To</th>
            <th scope="col">Who says so</th>
            <th scope="col">First attested</th>
            <th scope="col">Ancient source</th>
            <th scope="col">Sources</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((edge) => {
            const otherKey = edge.from === node.key ? edge.to : edge.from;
            const other = atlas.nodesByKey.get(otherKey);
            if (!other) return null;
            return (
              <tr key={edge.id} data-testid={`node-table-row-${otherKey}`} data-sacred={edge.sacred}>
                <td>{edge.label}</td>
                <th scope="row">
                  <span className="atlas__swatch" style={{ background: colorFor(other.group) }} aria-hidden="true" />
                  <a
                    href={hrefFor(otherKey)}
                    data-correspondence-key={otherKey}
                    data-attested={edge.earliestDisplay ? `first attested ${edge.earliestDisplay}` : "no date established"}
                  >
                    {other.name}
                  </a>
                  {/* The same affordance the kind tables carry: open the table
                      for this thing, wherever you met it. */}
                  <a
                    className="correspondence-table__open"
                    href={`${hrefFor(otherKey)}?view=table`}
                    title={`Open the table for ${other.name}`}
                    aria-label={`Open the table for ${other.name}`}
                    data-testid={`open-table-${otherKey}`}
                  >
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none"
                         stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                      <rect x="3.5" y="4.5" width="17" height="15" rx="1.2" />
                      <path d="M3.5 9.5h17M9.5 9.5V19.5M15 9.5V19.5" />
                    </svg>
                  </a>
                </th>
                <td>
                  {edge.schools.map((slug) => schools?.[slug]?.name || slug).join(", ") || (
                    <span className="correspondence-table__empty">—</span>
                  )}
                </td>
                <td>{edge.earliestDisplay || <span className="correspondence-table__empty">—</span>}</td>
                <td>
                  <span className={`node-table__sacred node-table__sacred--${edge.sacred}`}>
                    {SACRED_CELL[edge.sacred] || SACRED_CELL.not_investigated}
                  </span>
                </td>
                <td>
                  <button
                    type="button"
                    className="node-table__sources"
                    data-testid={`node-table-evidence-${edge.id}`}
                    onClick={() => onSelectEdge(edge)}
                  >
                    {edge.sourceCount === 0 ? "none located" : `${edge.sourceCount} — read them`}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="correspondence-table__note">
        Every kind of thing here has a table of its own:{" "}
        {[...new Set(rows.map((edge) => {
          const otherKey = edge.from === node.key ? edge.to : edge.from;
          return atlas.nodesByKey.get(otherKey)?.group;
        }).filter(Boolean))].map((group, index) => (
          <React.Fragment key={group}>
            {index > 0 ? ", " : ""}
            <a href={`/correspondences/tables/${group}`}>{labelFor(group).toLowerCase()}</a>
          </React.Fragment>
        ))}.
      </p>
    </div>
  );
}
